/**
 * @file ResponsiveListCompact.tsx
 * @description Компактная раскладка `ResponsiveList` (до 1024; phase19.31,
 * шаг О4; uiux-design.md §4): строка 72 (или 88 — «высокая»), вся строка —
 * ссылка, «⋯» 48px справа отдельной кнопкой.
 *
 * Строка собирается из тех же колонок по их роли `compact`:
 *   основная линия — `title` слева, `trailing` справа;
 *   вторая — `subtitle`; третья — `meta` через « · »; под ними — ряд
 *   кнопок `action` (видимое действие строки, без ссылки на всю строку).
 * Основная линия — текст 14–16/500, остальные — подпись вторым цветом.
 *
 * `wrap` (приёмка QA-5 D4): длинные названия (сделки) не обрезаются —
 * основная линия до двух строк, вторая и третья переносятся, а не уходят за
 * край («▲ 12 дн» срока на 360 больше не теряется).
 */

import { Link } from "react-router";
import { Fragment, type ReactNode } from "react";

import { LIST_FRAME_CLASS } from "./ListStates";
import type { CompactRole, ListColumn } from "./responsive-list-types";
import { RowActionsMenu, type RowAction } from "./RowActionsMenu";
import { cn } from "@/lib/utils";

export interface ListCompactProps<T> {
  rows: T[];
  columns: ListColumn<T>[];
  rowKey: (row: T) => string;
  rowHref?: (row: T) => string;
  rowLabel?: (row: T) => string;
  rowActions?: (row: T) => RowAction[];
  rowActionsTitle?: (row: T) => string;
  /** `tall` — строка 88 (три линии), `default` — 72. */
  rowSize: "default" | "tall";
  ariaLabel: string;
  /** Рамка списка; `false` — список уже внутри карточки (блок сводки). */
  framed?: boolean;
  /** Переносить линии вместо обрезки: название до двух строк, подписи — целиком. */
  wrap?: boolean;
}

/** Содержимое колонок одной роли (пустые пропускаем). */
function cellsOf<T>(row: T, columns: ListColumn<T>[], role: CompactRole): ReactNode[] {
  return columns
    .filter((col) => col.compact === role)
    .map((col) => (col.compactCell ?? col.cell)(row))
    .filter((node) => node !== null && node !== undefined && node !== false && node !== "");
}

/** Узлы подряд — у каждого свой ключ (массив без ключей React не любит). */
function keyed(nodes: ReactNode[]): ReactNode {
  return nodes.map((node, index) => <Fragment key={index}>{node}</Fragment>);
}

/** Узлы через « · » — для третьей линии. */
function joined(nodes: ReactNode[]): ReactNode {
  return nodes.map((node, index) => (
    <span key={index}>
      {index > 0 ? " · " : null}
      {node}
    </span>
  ));
}

export function ResponsiveListCompact<T>({
  rows,
  columns,
  rowKey,
  rowHref,
  rowLabel,
  rowActions,
  rowActionsTitle,
  rowSize,
  ariaLabel,
  framed = true,
  wrap = false,
}: ListCompactProps<T>) {
  // Обрезка многоточием или перенос — по признаку списка.
  const titleCls = wrap ? "line-clamp-2 break-words" : "truncate";
  const lineCls = wrap ? "break-words" : "truncate";
  return (
    <ul
      aria-label={ariaLabel}
      // Без рамки строки выходят к краям карточки (-mx), наведение — со скруглением.
      className={cn(framed ? LIST_FRAME_CLASS : "-mx-2 [&_a]:rounded-lg", "divide-y divide-[var(--border)]")}
    >
      {rows.map((row) => {
        const href = rowHref?.(row);
        const actions = rowActions?.(row) ?? [];
        const title = cellsOf(row, columns, "title");
        const trailing = cellsOf(row, columns, "trailing");
        const subtitle = cellsOf(row, columns, "subtitle");
        const meta = cellsOf(row, columns, "meta");
        const action = cellsOf(row, columns, "action");
        const body = (
          <>
            <span className={cn("flex min-w-0 justify-between gap-2", wrap ? "items-start" : "items-center")}>
              <span className={cn("type-body min-w-0 font-medium text-on-surface", titleCls)}>{keyed(title)}</span>
              {trailing.length > 0 ? <span className="flex shrink-0 items-center gap-2">{keyed(trailing)}</span> : null}
            </span>
            {subtitle.length > 0 ? (
              <span className={cn("type-caption min-w-0 text-on-surface", lineCls)}>{joined(subtitle)}</span>
            ) : null}
            {meta.length > 0 ? (
              <span className={cn("type-caption min-w-0 text-on-surface-variant", lineCls)}>{joined(meta)}</span>
            ) : null}
            {action.length > 0 ? <span className="mt-1 flex min-w-0 flex-wrap gap-2">{keyed(action)}</span> : null}
          </>
        );
        const bodyCls = cn(
          "flex min-w-0 flex-1 flex-col justify-center gap-0.5 py-2",
          framed ? "px-3" : "px-2",
          rowSize === "tall" ? "min-h-22" : "min-h-18",
        );
        return (
          <li key={rowKey(row)} className="flex items-stretch">
            {href ? (
              <Link
                to={href}
                aria-label={rowLabel?.(row)}
                className={cn(bodyCls, "outline-none transition-colors hover:bg-[var(--row-hover)] active:bg-surface-low focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring")}
              >
                {body}
              </Link>
            ) : (
              <div className={bodyCls}>{body}</div>
            )}
            {actions.length > 0 ? (
              <div className="flex shrink-0 items-center pr-2">
                <RowActionsMenu
                  actions={actions}
                  size="compact"
                  title={rowActionsTitle?.(row) ?? "Действия"}
                  ariaLabel={rowActionsTitle?.(row) ?? "Действия"}
                />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
